import React, { useState } from 'react';
import { useAppDispatch } from '../hooks/redux';
import { submitCompanyInquiry } from '../features/auth/authSlice';
import { showToast } from '../features/ui/uiSlice';

interface CompanyInquiryFormProps {
    onSuccess?: () => void;
}

const CompanyInquiryForm: React.FC<CompanyInquiryFormProps> = ({ onSuccess }) => {
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState({
        companyName: '',
        contactPersonName: '',
        email: '',
        phone: '',
        industry: '',
        companySize: '',
        website: '',
        description: '',
        businessType: '',
        expectedBudget: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const inquiryData = {
                ...formData,
                expectedBudget: parseFloat(formData.expectedBudget),
            };

            await dispatch(submitCompanyInquiry(inquiryData)).unwrap();

            dispatch(showToast({
                message: 'Company inquiry submitted successfully! We will contact you soon.',
                type: 'success'
            }));

            // Reset form
            setFormData({
                companyName: '',
                contactPersonName: '',
                email: '',
                phone: '',
                industry: '',
                companySize: '',
                website: '',
                description: '',
                businessType: '',
                expectedBudget: '',
            });

            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            dispatch(showToast({
                message: error || 'Failed to submit company inquiry',
                type: 'error'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Company Inquiry Form</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                            Company Name *
                        </label>
                        <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="contactPersonName" className="block text-sm font-medium text-gray-700 mb-2">
                            Contact Person Name *
                        </label>
                        <input
                            type="text"
                            id="contactPersonName"
                            name="contactPersonName"
                            value={formData.contactPersonName}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number *
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            pattern="[0-9]{10}"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                            Industry *
                        </label>
                        <input
                            type="text"
                            id="industry"
                            name="industry"
                            value={formData.industry}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-2">
                            Company Size *
                        </label>
                        <select
                            id="companySize"
                            name="companySize"
                            value={formData.companySize}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Company Size</option>
                            <option value="1-10">1-10 employees</option>
                            <option value="11-50">11-50 employees</option>
                            <option value="51-200">51-200 employees</option>
                            <option value="201-1000">201-1000 employees</option>
                            <option value="1000+">1000+ employees</option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                            Website
                        </label>
                        <input
                            type="url"
                            id="website"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
                            Business Type *
                        </label>
                        <select
                            id="businessType"
                            name="businessType"
                            value={formData.businessType}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Business Type</option>
                            <option value="scholarship">Scholarship Distribution</option>
                            <option value="promotion">Marketing & Promotion</option>
                            <option value="survey">Survey & Research</option>
                            <option value="data_collection">Data Collection</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div>
                        <label htmlFor="expectedBudget" className="block text-sm font-medium text-gray-700 mb-2">
                            Expected Budget (₹) *
                        </label>
                        <input
                            type="number"
                            id="expectedBudget"
                            name="expectedBudget"
                            value={formData.expectedBudget}
                            onChange={handleChange}
                            required
                            min="1000"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Description *
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe your company and what you're looking for..."
                    />
                    <p className="text-sm text-gray-500 mt-1">
                        {formData.description.length}/500 characters
                    </p>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CompanyInquiryForm;
